import * as THREE from 'three';
import type { Viewport } from './viewport.ts';

export interface Environment {
  grid: THREE.GridHelper;
  toggleGrid: (visible: boolean) => void;
  updateCameraFOV: (fov: number) => void;
  setBackground: (color: number) => void;
  /** Show/hide the soft ground shadow (shadow-catcher plane + light casting). */
  setGroundShadow: (on: boolean) => void;
  /** Ground-shadow darkness 0–1. */
  setShadowOpacity: (opacity: number) => void;
  /** Aim the key light: azimuth 0–360° around Y, elevation above the horizon. */
  setLightAngles: (azimuthDeg: number, elevationDeg: number) => void;
}

/** Distance of the key light from the origin (direction matters, not distance). */
const LIGHT_RADIUS = 12;

/**
 * Ground reference grid at Y=0 plus camera helpers. Grid visibility and FOV both
 * mutate scene/camera state without resizing the canvas (SPEC §6 / §3.3).
 * Also owns the ground-shadow rig: a transparent shadow-catcher plane and the
 * key light's shadow camera. The plane's ShadowMaterial only darkens where a
 * shadow falls, so it composites cleanly over any background — including the
 * transparent video-keying mode, where it acts as a proper shadow catcher.
 */
export function createEnvironment(vp: Viewport): Environment {
  const grid = new THREE.GridHelper(20, 20, 0x888888, 0x444444);
  // Push grid material slightly to avoid z-fighting with shadow/contact.
  (grid.material as THREE.Material).depthWrite = false;
  vp.scene.add(grid);

  // Shadow-catcher ground plane (invisible except where shadows land).
  const shadowPlane = new THREE.Mesh(
    new THREE.PlaneGeometry(40, 40),
    new THREE.ShadowMaterial({ opacity: 0.35 })
  );
  shadowPlane.rotation.x = -Math.PI / 2;
  shadowPlane.receiveShadow = true;
  shadowPlane.visible = false;
  vp.scene.add(shadowPlane);

  // Key-light shadow camera: covers the capture volume around the origin.
  const light = vp.keyLight;
  light.shadow.mapSize.set(2048, 2048);
  light.shadow.camera.left = -8;
  light.shadow.camera.right = 8;
  light.shadow.camera.top = 8;
  light.shadow.camera.bottom = -8;
  light.shadow.camera.near = 0.5;
  light.shadow.camera.far = 40;
  light.shadow.bias = -0.0005;
  light.shadow.normalBias = 0.02;

  function toggleGrid(visible: boolean): void {
    grid.visible = visible;
  }

  function setGroundShadow(on: boolean): void {
    light.castShadow = on;
    shadowPlane.visible = on;
  }

  function setShadowOpacity(opacity: number): void {
    (shadowPlane.material as THREE.ShadowMaterial).opacity = Math.min(Math.max(opacity, 0), 1);
  }

  function setLightAngles(azimuthDeg: number, elevationDeg: number): void {
    const az = (azimuthDeg * Math.PI) / 180;
    const el = (elevationDeg * Math.PI) / 180;
    light.position.set(
      LIGHT_RADIUS * Math.sin(az) * Math.cos(el),
      LIGHT_RADIUS * Math.sin(el),
      LIGHT_RADIUS * Math.cos(az) * Math.cos(el)
    );
  }

  function updateCameraFOV(fov: number): void {
    vp.camera.fov = fov;
    vp.camera.updateProjectionMatrix();
  }

  function setBackground(color: number): void {
    (vp.scene.background as THREE.Color).set(color);
  }

  return {
    grid,
    toggleGrid,
    updateCameraFOV,
    setBackground,
    setGroundShadow,
    setShadowOpacity,
    setLightAngles,
  };
}
